/**
 * Thị trường của bản build — cờ BUILD-TIME `VITE_MARKET` (`us` mặc định, `au` cho nhánh `aud`).
 *
 * `au` CHỈ ẩn điểm chạm UI: route của các màn dưới đây không được đăng ký (gõ URL cũng ra 404),
 * nav/thẻ Setup trỏ tới chúng bị lọc. Code của màn giữ nguyên, BE + DB không đổi — bỏ cờ là màn
 * sống lại (cùng cách `ENABLE_PAID_PLANS` đang làm với pricing/billing-detail).
 *
 * Phạm vi do owner chốt 13/10/2026 (roadmap `2026-Q3-aud-australia-pilot` bản 4.1): cắt
 * promotion/banner/spin, màn Turn Tracker, thu phí SaaS, website tiệm; Front Desk thay
 * employee-checkin · customer-checkin-list · book-appointment · create-appointment. (checkout-v2 left this list
 * when STORY-932 stopped registering its route at all — it is no longer a screen to hide.)
 */
import {
  bannerManagementRoutes,
  billingDetailRoutes,
  bookAppointmentRoutes,
  createAppointmentRoutes,
  createSpinCampaignRoutes,
  customerCheckinListRoutes,
  editSpinCampaignRoutes,
  employeeCheckinRoutes,
  pricingFreeRoutes,
  pricingRoutes,
  promotionBannerRoutes,
  promotionManagementRoutes,
  setupStoreLandingRoutes,
  spinCampaignDetailRoutes,
  spinCampaignsRoutes,
  spinCustomerRoutes,
  turnTrackerRoutes,
} from '@/pages';

export type Market = 'us' | 'au';

export const resolveMarket = (raw: unknown): Market => (raw === 'au' ? 'au' : 'us');

export const MARKET: Market = resolveMarket(import.meta.env.VITE_MARKET);

export const IS_MARKET_AU = MARKET === 'au';

/** Thẻ Setup không có route riêng (vd "Online booking") — ẩn theo `key`. */
export const AU_HIDDEN_SETUP_CARD_KEYS: readonly string[] = ['website', 'onlineBooking'];

// Hàm chứ không phải hằng: `@/pages` và các màn import lẫn nhau, đọc route lúc gọi tránh TDZ.
export const auHiddenRouteNames = (): ReadonlySet<string> =>
  new Set<string>([
    turnTrackerRoutes.name,
    promotionBannerRoutes.name,
    promotionManagementRoutes.name,
    bannerManagementRoutes.name,
    spinCampaignsRoutes.name,
    createSpinCampaignRoutes.name,
    editSpinCampaignRoutes.name,
    spinCampaignDetailRoutes.name,
    spinCustomerRoutes.name,
    pricingRoutes.name,
    pricingFreeRoutes.name,
    billingDetailRoutes.name,
    setupStoreLandingRoutes.name,
    employeeCheckinRoutes.name,
    customerCheckinListRoutes.name,
    bookAppointmentRoutes.name,
    createAppointmentRoutes.name,
  ]);

/** Route/nav có hiện ở thị trường `market` không. Route không tên luôn hiện. */
export const isRouteVisible = (name: unknown, market: Market = MARKET): boolean =>
  market !== 'au' || typeof name !== 'string' || !auHiddenRouteNames().has(name);

export const isSetupCardVisible = (
  card: { key: string; routeName?: string },
  market: Market = MARKET,
): boolean =>
  market !== 'au' || (!AU_HIDDEN_SETUP_CARD_KEYS.includes(card.key) && isRouteVisible(card.routeName, market));
